import React from 'react';
import { Type, Table, TableRow, TableCell } from '@spotify-internal/encore-creator-web';
import styled from 'styled-components';
import { useMe } from '../../../hooks/useMe';
import * as api from '../../../lib/Api';
import { MeShows } from '../../../types';
import { formatByOrderOfMagnitude } from '../../../lib/Formatter';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

type Props = {
  networkId: string | null;
  shows: MeShows[] | null;
};

export function AllTimeStats(props: Props) {
  const me = useMe();
  const hasNetworkPermissions = me!.permissions.network;

  const [alltimeStats, setAllTimeStats] = React.useState<AllTimeStats | null>(null);

  React.useEffect(() => {
    calculateAllTimeStats({
      hasNetworkPermissions,
      networkId: props.networkId,
      shows: props.shows,
    }).then(allTime => setAllTimeStats(allTime!));
  }, [hasNetworkPermissions, props.networkId, props.shows]);

  const intl = useIntl();
  const allTimeLabelMessages = defineMessages({
    starts: {
      id: 'home.all-time-starts',
      defaultMessage: 'starts',
    },
    streams: {
      id: 'home.all-time-streams',
      defaultMessage: 'streams',
    },
    listeners: {
      id: 'home.all-time-listeners',
      defaultMessage: 'listeners',
    },
    followers: {
      id: 'home.all-time-followers',
      defaultMessage: 'followers',
    },
  });

  return (
    <div>
      <Heading>
        <FormattedMessage id="home.all-time-title" defaultMessage="All time" />
      </Heading>
      <Table>
        <tbody>
          {alltimeStats &&
            Object.entries(alltimeStats!).map(([label, value]) => (
              <TableRow key={label} style={{ height: '68px' }}>
                <TableCell highlight condensed>
                  <span style={{ textTransform: 'capitalize' }}>
                    {intl.formatMessage(allTimeLabelMessages[label])}
                  </span>
                </TableCell>
                <TableCell numerical align="right" style={{ paddingRight: 0 }}>
                  {value && formatByOrderOfMagnitude(value)}
                </TableCell>
              </TableRow>
            ))}
        </tbody>
      </Table>
    </div>
  );
}

const Heading = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading3,
})`
  padding-bottom: 17px;
  border-bottom: 1px solid #979797;
`;

type AllTimeStats = {
  starts: number;
  streams: number;
  listeners: number;
  followers?: number;
};

async function calculateAllTimeStats({
  hasNetworkPermissions,
  networkId,
  shows,
}: {
  hasNetworkPermissions: boolean;
  networkId: string | null;
  shows: MeShows[] | null;
}): Promise<AllTimeStats> {
  if (hasNetworkPermissions && networkId) {
    return await api.loadNetworkAllTimeStats(networkId);
  }

  return (shows || []).reduce<AllTimeStats>(
    ({ starts, streams, listeners }, show) => {
      return {
        starts: starts + (show.starts || 0),
        streams: streams + (show.streams || 0),
        listeners: listeners + (show.listeners || 0),
      };
    },
    {
      starts: 0,
      streams: 0,
      listeners: 0,
    },
  );
}
